<template>
  <div v-if="!loading">
    <b-row class="flex-column">
      <b-col>
        <h2 class="font-weight-bold text-dark">
          {{ $t('remission') }}
        </h2>
      </b-col>
    </b-row>
    <b-row class="flex-column">
      <b-col
        sm="12"
        class="d-flex justify-content-end"
      >
        <b-badge
          :variant="$helpers.shippingStatusColor(purchaseOrder.shipping_status)"
          class="h3"
        >
          {{ $helpers.shippingStatusName(purchaseOrder.shipping_status) }}
        </b-badge>
      </b-col>
    </b-row>
    <b-row class="mt-2">
      <b-col
        sm="12"
        class="mt-3 text-center"
      >
        <b-img
          :src="
            require('@/assets/images/yoconstruyo/imagen-datos-bancarios.png')
          "
        />
      </b-col>
    </b-row>

    <b-row class="mt-3 justify-content-center">
      <b-col
        sm="12"
        lg="8"
      >
        <b-card
          no-body
          class="remissions-card"
        >
          <b-card-header class="d-flex justify-content-between align-items-center">
            <h4 class="mb-0">
              {{ $t('remissionsUploaded') }}
              <b-badge
                v-if="activeRemissions.length"
                variant="light-primary"
                class="ml-50"
              >
                {{ activeRemissions.length }}
              </b-badge>
            </h4>
            <b-button
              v-if="activeRemissions.length"
              size="sm"
              variant="outline-primary"
              @click="downloadRemissions"
            >
              <feather-icon
                icon="DownloadIcon"
                class="mr-50"
              />
              {{ $t('downloadAll') }}
            </b-button>
          </b-card-header>
          <b-list-group
            v-if="remissions.length"
            flush
          >
            <b-list-group-item
              v-for="remission in remissions"
              :key="remission.id"
              :class="['d-flex align-items-center', { 'remission--rejected': remission.rejected }]"
            >
              <feather-icon
                :icon="remission.rejected ? 'FileMinusIcon' : 'FileTextIcon'"
                size="20"
                class="mr-1 flex-shrink-0"
              />
              <div class="flex-grow-1 min-w-0">
                <a
                  :href="remission.url"
                  target="_blank"
                  rel="noopener"
                  class="d-block text-truncate font-weight-bold"
                >
                  {{ remission.filename }}
                </a>
                <small class="text-muted">
                  {{ $t('uploadedAt') }} {{ remission.created_at | date }}
                </small>
              </div>
              <b-badge
                v-if="remission.rejected"
                variant="light-danger"
                class="ml-1"
              >
                {{ $t('rejectedRemission') }}
              </b-badge>
              <b-button
                v-else-if="canDelete"
                size="sm"
                variant="flat-danger"
                class="btn-icon ml-1"
                :disabled="deletingId === remission.id"
                @click="askDelete(remission)"
              >
                <b-spinner
                  v-if="deletingId === remission.id"
                  small
                />
                <feather-icon
                  v-else
                  icon="Trash2Icon"
                />
              </b-button>
            </b-list-group-item>
          </b-list-group>
          <b-card-body
            v-else
            class="text-center text-muted"
          >
            {{ $t('noRemissionsYet') }}
          </b-card-body>
        </b-card>
      </b-col>
    </b-row>

    <b-row
      v-if="canUpload"
      class="mt-2 justify-content-center"
    >
      <b-col
        sm="12"
        class="p-0 text-center"
      >
        <h3 class="text-dark">
          {{ $t('uploadRemission') }}:
        </h3>
        <p
          v-if="activeRemissions.length"
          class="text-muted"
        >
          {{ $t('addMoreRemissions') }}
        </p>
      </b-col>
      <b-col
        sm="12"
        lg="4"
        class="p-0 text-center mt-1"
      >
        <dropzone
          :height="'150'"
          :multiple="true"
        />
      </b-col>
    </b-row>

    <b-row
      v-if="currentUser.role_name === 'admin'"
      class="mt-2"
    >
      <b-col class="text-right">
        <b-button
          v-if="purchaseOrder.shipping_status === 'awaiting_shipping_approval'"
          v-b-modal.confirmReject
          variant="outline-danger"
          class="mb-75"
        >
          {{ $t('reject') }}
        </b-button>
      </b-col>
      <b-col class="text-left">
        <b-button
          v-if="purchaseOrder.shipping_status === 'awaiting_shipping_approval'"
          v-b-modal.confirmApprove
          variant="outline-success"
          class="mb-75"
        >
          {{ $t('approve') }}
        </b-button>
      </b-col>
      <confirmation-modal
        v-if="purchaseOrder.shipping_status === 'awaiting_shipping_approval'"
        :id="'confirmApprove'"
        :variant="'success'"
        @confirm="approveShip"
      >
        <template v-slot:content>
          {{ $t('approveShipmentConfirmation') }}
        </template>
      </confirmation-modal>

      <confirmation-modal
        v-if="purchaseOrder.shipping_status === 'awaiting_shipping_approval'"
        :id="'confirmReject'"
        :variant="'danger'"
        @confirm="rejectShip"
      >
        <template v-slot:content>
          {{ $t('rejectShipmentConfirmation') }}
        </template>
      </confirmation-modal>
    </b-row>

    <confirmation-modal
      :id="'confirmDeleteRemission'"
      :variant="'danger'"
      @confirm="confirmDelete"
      @cancel="remissionToDelete = null"
    >
      <template v-slot:content>
        {{ $t('deleteRemissionConfirmation') }}
        <div
          v-if="remissionToDelete"
          class="mt-1 font-weight-bold"
        >
          {{ remissionToDelete.filename }}
        </div>
      </template>
    </confirmation-modal>

    <b-row
      v-if="newProviderFiles.length > 0"
      class="mt-2 justify-content-center"
    >
      <b-col>
        <files-table :files="newProviderFiles" />
      </b-col>
    </b-row>
    <b-row
      v-if="newProviderFiles.length > 0"
      class="mt-2 justify-content-center"
    >
      <b-col class="text-center">
        <b-button
          variant="primary"
          :disabled="uploading"
          @click="upload()"
        >
          <b-spinner
            v-if="uploading"
            small
            class="mr-50"
          />
          {{ $t('uploadRemission') }}
        </b-button>
      </b-col>
    </b-row>
    <b-row class="mt-2">
      <b-col
        sm="12"
        class="p-0 text-center"
      >
        <h3 class="text-primary">
          {{ $t('contactInfo') }}:
        </h3>
        <h3 class="text-dark">
          contacto@yoconstruyo.com.mx
        </h3>
      </b-col>
    </b-row>
  </div>
</template>

<script>
import {
  mapState, mapGetters, mapActions, mapMutations,
} from 'vuex'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'
import Dropzone from '@/components/dropzone/Dropzone.vue'
import FilesTable from '@/components/tables/FilesTable.vue'
import ConfirmationModal from '@/components/ConfirmationModal.vue'

export default {
  components: {
    Dropzone,
    FilesTable,
    ConfirmationModal,
  },

  data() {
    return {
      loading: false,
      uploading: false,
      deletingId: null,
      remissionToDelete: null,
    }
  },

  computed: {
    ...mapGetters('suppliers', ['suppliers', 'newProviderFiles']),
    ...mapGetters('purchaseOrders', ['purchaseOrders', 'purchaseOrder']),
    ...mapGetters('auth', ['currentUser']),
    ...mapState(['apiUrl']),

    remissions() {
      return this.purchaseOrder.remissions || []
    },

    activeRemissions() {
      return this.remissions.filter(remission => !remission.rejected)
    },

    canUpload() {
      return this.purchaseOrder.shipping_status !== 'shipping_approved'
    },

    canDelete() {
      return this.canUpload
    },
  },

  created() {
    this.loading = true
    this.fetchPurchaseOrder(this.$route.params.purchaseOrderId).then(() => {
      this.loading = false
    })
  },

  methods: {
    ...mapActions('purchaseOrders', [
      'fetchPurchaseOrder',
      'fetchPurchaseOrders',
      'shipPurchaseOrder',
      'approveShipment',
      'rejectShipment',
      'downloadPurchaseOrderRemissions',
      'deletePurchaseOrderRemission',
    ]),
    ...mapMutations('suppliers', { clearFiles: 'CLEAR_FILES' }),

    notify(title, variant, icon = 'FileTextIcon') {
      this.$toast({
        component: ToastificationContent,
        props: { title, icon, variant },
      })
    },

    downloadRemissions() {
      this.downloadPurchaseOrderRemissions(this.purchaseOrder)
    },

    upload() {
      const formData = new FormData()
      this.newProviderFiles.forEach(file => {
        formData.append('document_attachment[]', file, file.name)
      })

      this.uploading = true
      this.shipPurchaseOrder({
        purchaseOrderId: this.purchaseOrder.id,
        attachments: formData,
      })
        .then(() => {
          this.clearFiles()
          this.notify(this.$t('remissionSubmitted'), 'success', 'UploadIcon')
        })
        .catch(error => {
          this.notify(error?.data?.messages?.[0] || this.$t('Error'), 'danger', 'AlertCircleIcon')
        })
        .finally(() => {
          this.uploading = false
        })
    },

    askDelete(remission) {
      this.remissionToDelete = remission
      this.$bvModal.show('confirmDeleteRemission')
    },

    confirmDelete() {
      const remission = this.remissionToDelete
      if (!remission) return

      this.deletingId = remission.id
      this.deletePurchaseOrderRemission({
        purchaseOrderId: this.purchaseOrder.id,
        attachmentId: remission.id,
      })
        .then(() => {
          this.notify(this.$t('remissionDeleted'), 'success', 'Trash2Icon')
        })
        .catch(error => {
          this.notify(error?.data?.messages?.[0] || this.$t('Error'), 'danger', 'AlertCircleIcon')
          return this.fetchPurchaseOrder(this.purchaseOrder.id)
        })
        .finally(() => {
          this.deletingId = null
          this.remissionToDelete = null
        })
    },

    approveShip() {
      this.approveShipment({
        purchaseOrderId: this.$route.params.purchaseOrderId,
      }).then(() => {
        this.notify(this.$t('shippingApproved'), 'success', 'CheckCircleIcon')
      })
    },

    rejectShip() {
      this.rejectShipment({
        purchaseOrderId: this.$route.params.purchaseOrderId,
      }).then(() => {
        this.notify(this.$t('shippingRejected'), 'warning', 'XCircleIcon')
      })
    },
  },
}
</script>

<style lang="scss" scoped>
.remissions-card .list-group-item {
  padding: 0.75rem 1.5rem;
}

.remission--rejected {
  opacity: 0.6;

  a {
    text-decoration: line-through;
  }
}

.min-w-0 {
  min-width: 0;
}
</style>
